import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import KPICards from './KPICards';
import TimeSeriesCharts from './TimeSeriesCharts';
import DetailedAnalysis from './DetailedAnalysis';
import ImprovementSuggestions from './ImprovementSuggestions';
import AnalysisFilters from './AnalysisFilters';
import SurveyAnalytics from './SurveyAnalytics';
import { FilterOptions } from '../../../types/analytics';
import { supabaseClient } from '../../utils/supabaseClient';

interface AnalysisPageProps {
  session: any;
}

const AnalysisPage: React.FC<AnalysisPageProps> = ({ session }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'reviews' | 'surveys'>('reviews');
  const [filters, setFilters] = useState<FilterOptions>({
    dateRange: {
      start: new Date(2000, 0, 1), // 全期間の開始日
      end: new Date() // 今日
    },
    stores: [''],
    ratings: [],
    periodLabel: '全期間'
  });
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [storeInitialized, setStoreInitialized] = useState(false);

  useEffect(() => {
    document.title = '分析 | RepliGo（レプリゴ）';
    
    // セッションストレージから選択された店舗を復元
    const savedStore = sessionStorage.getItem('selectedStore');
    if (savedStore) {
      setFilters(prev => ({
        ...prev,
        stores: [savedStore]
      }));
      setStoreInitialized(true);
    } else {
      // URLのクエリパラメータから店舗名を取得（下位互換性のため）
      const storeFromUrl = searchParams.get('store');
      if (storeFromUrl) {
        const decodedStore = decodeURIComponent(storeFromUrl);
        setFilters(prev => ({
          ...prev,
          stores: [decodedStore]
        }));
        // セッションストレージにも保存
        sessionStorage.setItem('selectedStore', decodedStore);
        setStoreInitialized(true);
      } else {
        setStoreInitialized(false);
      }
    }
    // ここでデータの読み込みを行う
    setIsLoading(false);
  }, [searchParams]);

  // 店舗情報を取得（URLに店舗指定がない場合のみ）
  useEffect(() => {
    const storeFromUrl = searchParams.get('store');
    if (storeFromUrl || storeInitialized) return; // URLに店舗指定がある場合、または既に初期化済みの場合はスキップ
    
    const fetchStore = async () => {
      try {
        const { data, error } = await supabaseClient
          .from('stores')
          .select('shopname')
          .eq('user_email', session.user.email)
          .limit(1);
        
        if (error) {
          console.error("Error fetching store:", error.message);
          return;
        }

        if (data && data.length > 0) {
          const defaultStore = data[0].shopname;
          setFilters(prev => ({
            ...prev,
            stores: [defaultStore]
          }));
          // セッションストレージにも保存
          sessionStorage.setItem('selectedStore', defaultStore);
          setStoreInitialized(true);
        }
      } catch (error) {
        console.error('Error in fetchStore:', error);
      }
    };
    
    if (session?.user?.email) {
      fetchStore();
    }
  }, [session, searchParams, storeInitialized]);

  const handleFilterChange = (newFilters: Partial<FilterOptions>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleRefresh = () => {
    setIsLoading(true);
    // ここでデータの再読み込みを行う
    setTimeout(() => {
      setIsLoading(false);
      setLastUpdated(new Date());
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center">
          <img src="/img/logo.png" alt="RepliGo（レプリゴ）" className="h-12" />
          <span className="ml-4 text-2xl sm:text-3xl font-bold text-gray-800">分析ダッシュボード</span>
        </div>
      </div>

      <main className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <AnalysisFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onRefresh={handleRefresh}
                isLoading={isLoading}
                lastUpdated={lastUpdated}
                userEmail={session.user.email}
              />
            </div>

            {/* タブナビゲーション */}
            <div className="mb-6">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab('reviews')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'reviews'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    口コミ分析
                  </button>
                  <button
                    onClick={() => setActiveTab('surveys')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'surveys'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    アンケート分析
                  </button>
                </nav>
              </div>
            </div>

            {/* コンテンツエリア */}
            {activeTab === 'reviews' ? (
              <>
                <KPICards isLoading={isLoading} shopname={filters.stores[0]} dateRange={filters.dateRange} />

                <TimeSeriesCharts isLoading={isLoading} shopname={filters.stores[0]} dateRange={filters.dateRange} />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <DetailedAnalysis isLoading={isLoading} shopname={filters.stores[0]} dateRange={filters.dateRange} periodLabel={filters.periodLabel} />
                  <ImprovementSuggestions 
                    isLoading={isLoading}
                    shopName={filters.stores[0]}
                    dateRange={filters.dateRange}
                    periodLabel={filters.periodLabel}
                  />
                </div>
              </>
            ) : (
              <SurveyAnalytics 
                shopName={filters.stores[0]}
                dateRange={filters.dateRange}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AnalysisPage; 